// ANGULAR
import { NgZone } from '@angular/core';

declare const KTWizard: any;

/**
 * Install a one-time global zone bridge over the legacy Metronic `KTWizard`
 * widget (loaded as a plain `<script>` in `index.html` — `KTWizard` is a
 * window global).
 *
 * Why
 * ---
 * Every `*-action` modal that uses the legacy step-wizard does:
 *   const wizard = new KTWizard(this.el.nativeElement, { startStep: 1 });
 *   wizard.on('beforeNext', (wizardObj) => { this.currentStep = …; });
 *
 * `KTWizard.on(...)` registers a callback that the widget invokes from its
 * internal click handler. While that click handler IS zone-patched (the
 * widget calls `addEventListener` while we're in the Angular zone), the
 * callback we pass to `.on(...)` can be invoked from internal jQuery-style
 * trigger paths or from promise-resolutions inside the wizard's own code,
 * which may not be in the same zone. The component-side state mutations
 * (`this.currentStep = …`, `this.loading = false`, etc.) then don't trigger
 * change detection — the classic "wizard step doesn't update until I click
 * again" symptom.
 *
 * Rather than wrap every single `wizard.on(...)` call site by hand (there are
 * 20+), we patch `KTWizard.prototype.on` once at app startup so every
 * callback registered via `.on(...)` is automatically wrapped in
 * `ngZone.run(...)`. New call sites added later get the fix for free.
 *
 * Idempotent: guarded by `__zone_bridge_installed` so re-installation is a
 * no-op (matters for tests and HMR).
 */
export function installKtWizardZoneBridge(ngZone: NgZone): void {
  const win = (typeof window !== 'undefined' ? window : undefined) as any;
  if (!win) {
    return;
  }
  const Wizard = win.KTWizard;
  if (!Wizard || !Wizard.prototype || typeof Wizard.prototype.on !== 'function') {
    // KTWizard isn't loaded (yet) — bail out silently. Metronic scripts are
    // loaded before the Angular bundle in index.html so this should not happen
    // in production, but guarding makes the bridge safe in tests / SSR.
    return;
  }
  if (Wizard.prototype.__zone_bridge_installed) {
    return;
  }

  const originalOn = Wizard.prototype.on;
  Wizard.prototype.on = function patchedOn(eventName: string, callback: (...args: any[]) => any) {
    if (typeof callback !== 'function') {
      return originalOn.call(this, eventName, callback);
    }
    return originalOn.call(this, eventName, (...args: any[]) => ngZone.run(() => callback(...args)));
  };

  Wizard.prototype.__zone_bridge_installed = true;
}
